/**
 * Web-specific voice recording and playback using MediaRecorder API.
 * 
 * Separated from useVoiceNotes to keep files under 300 lines.
 * On web, expo-av Recording is broken (blob URIs can't be read by FileSystem).
 */

import { useCallback, useRef } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

interface WebRecordingRefs {
  mediaRecorder: MediaRecorder | null;
  chunks: Blob[];
  stream: MediaStream | null;
  audioEl: HTMLAudioElement | null;
}

interface WebAudioCallbacks {
  onRecordingStarted: () => void;
  onRecordingStopped: () => void;
  onUploadStart: () => void;
  onUploadEnd: () => void;
  onError: (msg: string) => void;
  onPlayStart: (noteId: number) => void;
  onPlayEnd: () => void;
  getLocation: () => Promise<{ latitude: number; longitude: number } | null>;
  getDuration: () => number;
  fetchNotes: () => Promise<void>;
}

export function useWebAudio(
  portfolioId: number,
  opportunityId: number,
  callbacks: WebAudioCallbacks,
) {
  const refs = useRef<WebRecordingRefs>({
    mediaRecorder: null,
    chunks: [],
    stream: null,
    audioEl: null,
  });

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        callbacks.onError('Audio recording not supported in this browser');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      refs.current.stream = stream;
      refs.current.chunks = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      refs.current.mediaRecorder = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) refs.current.chunks.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(refs.current.chunks, { type: mimeType });
        stream.getTracks().forEach(t => t.stop());
        refs.current.stream = null;
        await uploadBlob(blob, mimeType);
      };

      recorder.start(250);
      callbacks.onRecordingStarted();
    } catch (err: any) {
      logger.error('Web recording failed to start', err, 'useVoiceNotes');
      callbacks.onError(err?.message || 'Failed to access microphone');
    }
  }, [portfolioId, opportunityId, callbacks]);

  const stopRecording = useCallback(async () => {
    callbacks.onRecordingStopped();
    if (refs.current.mediaRecorder && refs.current.mediaRecorder.state !== 'inactive') {
      callbacks.onUploadStart();
      refs.current.mediaRecorder.stop();
    }
  }, [callbacks]);

  const uploadBlob = useCallback(async (blob: Blob, mimeType: string) => {
    try {
      callbacks.onUploadStart();
      const base64 = await blobToBase64(blob);
      const location = await callbacks.getLocation();
      const durationMs = callbacks.getDuration() * 1000;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const ext = mimeType.includes('webm') ? 'webm' : 'm4a';

      await authApi.uploadVoiceNote(portfolioId, opportunityId, {
        audio_data: base64,
        filename: `voice-note-${timestamp}.${ext}`,
        mime_type: mimeType.split(';')[0],
        duration_ms: durationMs,
        latitude: location?.latitude,
        longitude: location?.longitude,
      });

      await callbacks.fetchNotes();
    } catch (err) {
      logger.error('Failed to upload web recording', err, 'useVoiceNotes');
      callbacks.onError('Failed to upload recording');
    } finally {
      callbacks.onUploadEnd();
    }
  }, [portfolioId, opportunityId, callbacks]);

  const playNote = useCallback(async (noteId: number) => {
    try {
      if (refs.current.audioEl) {
        refs.current.audioEl.pause();
        refs.current.audioEl = null;
      }
      callbacks.onPlayStart(noteId);
      const response = await authApi.getVoiceNote(portfolioId, opportunityId, noteId);
      if (!response.success || !response.voiceNote.audio_data) {
        callbacks.onPlayEnd();
        return;
      }

      const mime = response.voiceNote.mime_type || 'audio/webm';
      const audio = new Audio(`data:${mime};base64,${response.voiceNote.audio_data}`);
      refs.current.audioEl = audio;
      audio.onended = () => { callbacks.onPlayEnd(); refs.current.audioEl = null; };
      audio.onerror = () => { callbacks.onPlayEnd(); refs.current.audioEl = null; callbacks.onError('Failed to play audio'); };
      await audio.play();
    } catch (err) {
      logger.error('Failed to play voice note', err, 'useVoiceNotes');
      callbacks.onPlayEnd();
    }
  }, [portfolioId, opportunityId, callbacks]);

  const stopPlayback = useCallback(() => {
    if (refs.current.audioEl) {
      refs.current.audioEl.pause();
      refs.current.audioEl = null;
    }
    callbacks.onPlayEnd();
  }, [callbacks]);

  const cleanup = useCallback(() => {
    if (refs.current.stream) refs.current.stream.getTracks().forEach(t => t.stop());
    if (refs.current.audioEl) refs.current.audioEl.pause();
  }, []);

  return { startRecording, stopRecording, playNote, stopPlayback, cleanup };
}
